import type { AtomContext, RenderLocals, SubmittedFormValues } from "./registry-types";

function isPlainObjectInternal(value: unknown): value is Record<string, unknown> {
 return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeBindingPath(input: string): string {
 return String(input ?? "")
 .trim()
 .replace(/^@/, "")
 .replace(/\[(\d+|"[^"]+"|'[^']+')\]/g, (_, raw: string) => {
 const key = String(raw).replace(/^["']|["']$/g, "");
 return `.${key}`;
 });
}

function readPathFromObject(root: unknown, pathStr: string): unknown {
 if (root === null || root === undefined) return undefined;

 const normalized = normalizeBindingPath(pathStr);
 if (!normalized) return root;

 const parts = normalized.split(".").filter(Boolean);
 if (parts.length ===0) return undefined;

 let cur: unknown = root;
 for (const seg of parts) {
 if (cur === null || cur === undefined || typeof cur !== "object") {
 return undefined;
 }
 cur = (cur as Record<string, unknown>)[seg];
 }

 return cur;
}

function shouldUseFallback(resolved: unknown): boolean {
 return resolved === null || resolved === undefined || resolved === "";
}

function resolveBindingReference(
 refValue: unknown,
 ctx: AtomContext,
 locals: RenderLocals,
): unknown {
 if (typeof refValue !== "string") return refValue;

 const normalized = normalizeBindingPath(refValue);
 if (!normalized) return undefined;

 const parts = normalized.split(".").filter(Boolean);
 const head = parts[0] ?? "";

 if (head && Object.prototype.hasOwnProperty.call(locals, head)) {
 const localRoot = locals[head];
 const tail = parts.slice(1).join(".");
 return tail ? readPathFromObject(localRoot, tail) : localRoot;
 }

 return ctx.resolveRef(refValue);
}

function looseEqual(a: unknown, b: unknown): boolean {
 if (a === b) return true;
 if (a === null || a === undefined) return b === null || b === undefined;
 if (typeof a === "number" && typeof b === "number") return a === b;
 return String(a) === String(b);
}

function evaluateCondition(
 condition: unknown,
 ctx: AtomContext,
 locals: RenderLocals,
): boolean {
 if (condition === null || condition === undefined) return false;

 if (typeof condition !== "object" || Array.isArray(condition)) {
 return Boolean(resolveRenderableValue(condition, ctx, locals));
 }

 const c = condition as Record<string, unknown>;

 if (typeof c.op === "string") {
 // "and"/"or" are accepted as authoring-friendly aliases of "&"/"|".
 const op = c.op === "and" ? "&" : c.op === "or" ? "|" : c.op;
 switch (op) {
 case "?":
 return Boolean(resolveRenderableValue(c.v, ctx, locals));
 case "!": {
 const next = c.v;
 if (next && typeof next === "object" && !Array.isArray(next) && "op" in next) {
 return !evaluateCondition(next, ctx, locals);
 }
 return !Boolean(resolveRenderableValue(next, ctx, locals));
 }
 case "&": {
 const list = Array.isArray(c.conditions) ? c.conditions : [];
 return list.every((cond) => evaluateCondition(cond, ctx, locals));
 }
 case "|": {
 const list = Array.isArray(c.conditions) ? c.conditions : [];
 return list.some((cond) => evaluateCondition(cond, ctx, locals));
 }
 case "=":
 return looseEqual(
 resolveRenderableValue(c.left ?? c.l, ctx, locals),
 resolveRenderableValue(c.right ?? c.r, ctx, locals),
 );
 case "!=":
 return !looseEqual(
 resolveRenderableValue(c.left ?? c.l, ctx, locals),
 resolveRenderableValue(c.right ?? c.r, ctx, locals),
 );
 case "<":
 case ">":
 case "<=":
 case ">=": {
 const a = resolveRenderableValue(c.left ?? c.l, ctx, locals);
 const b = resolveRenderableValue(c.right ?? c.r, ctx, locals);
 const an = typeof a === "number" ? a : Number(a);
 const bn = typeof b === "number" ? b : Number(b);
 if (Number.isNaN(an) || Number.isNaN(bn)) return false;
 switch (op) {
 case "<":
 return an< bn;
 case ">":
 return an > bn;
 case "<=":
 return an <= bn;
 case ">=":
 return an >= bn;
 }
 }
 case "in": {
 const needle = resolveRenderableValue(c.left ?? c.l, ctx, locals);
 const haystack = resolveRenderableValue(c.right ?? c.r, ctx, locals);
 if (Array.isArray(haystack)) return haystack.includes(needle);
 if (typeof haystack === "string" && typeof needle === "string") {
 return haystack.includes(needle);
 }
 return false;
 }
 case "match": {
 const value = resolveRenderableValue(c.left ?? c.l, ctx, locals);
 const pattern = resolveRenderableValue(c.right ?? c.r, ctx, locals);
 if (typeof value !== "string" || typeof pattern !== "string") return false;
 try {
 return new RegExp(pattern).test(value);
 } catch {
 return false;
 }
 }
 default:
 return false;
 }
 }

 if (Array.isArray(c.$eq) && c.$eq.length ===2) {
 return looseEqual(
 resolveRenderableValue(c.$eq[0], ctx, locals),
 resolveRenderableValue(c.$eq[1], ctx, locals),
 );
 }

 if (Array.isArray(c.$and)) {
 return c.$and.every((cond) => evaluateCondition(cond, ctx, locals));
 }

 if (Array.isArray(c.$or)) {
 return c.$or.some((cond) => evaluateCondition(cond, ctx, locals));
 }

 if (Object.prototype.hasOwnProperty.call(c, "$not")) {
 return !evaluateCondition(c.$not, ctx, locals);
 }

 return Boolean(resolveRenderableValue(condition, ctx, locals));
}

function resolveConditionalValue(
 value: Record<string, unknown>,
 ctx: AtomContext,
 locals: RenderLocals,
): unknown {
 const hasLegacyBranches =
 Object.prototype.hasOwnProperty.call(value, "then") ||
 Object.prototype.hasOwnProperty.call(value, "else");

 if (hasLegacyBranches) {
 const condition = evaluateCondition(value.$if, ctx, locals);
 if (condition) return resolveRenderableValue(value.then, ctx, locals);
 if (Object.prototype.hasOwnProperty.call(value, "else")) {
 return resolveRenderableValue(value.else, ctx, locals);
 }
 return undefined;
 }

 const conditional = value.$if;

 if (
 isPlainObjectInternal(conditional) &&
 typeof conditional.op === "string" &&
 (Object.prototype.hasOwnProperty.call(conditional, "then") ||
 Object.prototype.hasOwnProperty.call(conditional, "else"))
 ) {
 const condition = evaluateCondition(conditional, ctx, locals);
 if (condition) return resolveRenderableValue(conditional.then, ctx, locals);
 if (Object.prototype.hasOwnProperty.call(conditional, "else")) {
 return resolveRenderableValue(conditional.else, ctx, locals);
 }
 return undefined;
 }

 if (
 isPlainObjectInternal(conditional) &&
 (Object.prototype.hasOwnProperty.call(conditional, "test") ||
 Object.prototype.hasOwnProperty.call(conditional, "then") ||
 Object.prototype.hasOwnProperty.call(conditional, "else"))
 ) {
 const condition = evaluateCondition(conditional.test, ctx, locals);
 if (condition) return resolveRenderableValue(conditional.then, ctx, locals);
 if (Object.prototype.hasOwnProperty.call(conditional, "else")) {
 return resolveRenderableValue(conditional.else, ctx, locals);
 }
 return undefined;
 }

 return evaluateCondition(conditional, ctx, locals);
}

/**
 * Resolve a `{ $t: "namespace.key" }` translation binding.
 *
 * The renderer's locale bundle is pre-merged by the server (active locale
 * over English), so this is a single lookup — the English fallback chain is
 * centralized at bundle-merge time, not repeated per binding.
 *
 * Bundle shapes vary by loading path: some leave the namespace wrapped
 * (`bundle.onboarding.continue`) while others unwrap the matching outer key
 * (`bundle.continue`). The direct path is tried first; when it fails, the
 * leading namespace segment is stripped and retried so text never silently
 * disappears because of the bundle shape.
 */
function resolveTranslationValue(
 value: Record<string, unknown>,
 ctx: AtomContext,
): unknown {
 const key = value.$t;
 if (typeof key !== "string" || !key.trim()) return "";

 const lookup = (lookupKey: string): unknown =>
 ctx.translate ? ctx.translate(lookupKey) : undefined;

 const translated = lookup(key);
 if (translated !== null && translated !== undefined) return translated;

 const segments = key.trim().split(".").filter(Boolean);
 if (segments.length > 1) {
 const unwrapped = lookup(segments.slice(1).join("."));
 if (unwrapped !== null && unwrapped !== undefined) return unwrapped;
 }

 return "";
}

export function resolveRenderableValue(
 value: unknown,
 ctx: AtomContext,
 locals: RenderLocals,
): unknown {
 if (value === null || value === undefined) return value;
 if (typeof value === "string") {
 if (value.startsWith("@")) {
 const bound = resolveBindingReference(value, ctx, locals);
 return shouldUseFallback(bound) ? undefined : bound;
 }
 return value;
 }
 if (typeof value === "number" || typeof value === "boolean") return value;

 if (Array.isArray(value)) {
 return value.map((item) => resolveRenderableValue(item, ctx, locals));
 }

 if (!isPlainObjectInternal(value)) return value;

 if (Object.prototype.hasOwnProperty.call(value, "$t")) {
 return resolveTranslationValue(value, ctx);
 }

 if (Object.prototype.hasOwnProperty.call(value, "$bind")) {
 const bound = resolveBindingReference(value.$bind, ctx, locals);
 if (
 shouldUseFallback(bound) &&
 Object.prototype.hasOwnProperty.call(value, "fallback")
 ) {
 return resolveRenderableValue(value.fallback, ctx, locals);
 }
 // A bound slot may itself hold an authored binding object — for example a
 // flow storing `{ $t: contact.errors.submitFailed }` in state. Return the
 // raw object here and the alert box rendered no readable text. Resolve the
 // bound value recursively so nested `$t`/`$bind`/`$if` objects produce the
 // primitive they represent.
 return resolveRenderableValue(bound, ctx, locals);
 }

 if (Object.prototype.hasOwnProperty.call(value, "$if")) {
 return resolveConditionalValue(value, ctx, locals);
 }

 if (Object.prototype.hasOwnProperty.call(value, "$eq")) {
 const tuple = value.$eq as readonly unknown[];
 if (tuple.length ===2) {
 return looseEqual(
 resolveRenderableValue(tuple[0], ctx, locals),
 resolveRenderableValue(tuple[1], ctx, locals),
 );
 }
 }

 if (Object.prototype.hasOwnProperty.call(value, "$and")) {
 const list = Array.isArray(value.$and) ? value.$and : [];
 return list.every((cond) => Boolean(resolveRenderableValue(cond, ctx, locals)));
 }

 if (Object.prototype.hasOwnProperty.call(value, "$or")) {
 const list = Array.isArray(value.$or) ? value.$or : [];
 return list.some((cond) => Boolean(resolveRenderableValue(cond, ctx, locals)));
 }

 if (Object.prototype.hasOwnProperty.call(value, "$not")) {
 return !Boolean(resolveRenderableValue(value.$not, ctx, locals));
 }

 if (typeof value.op === "string") {
 return evaluateCondition(value, ctx, locals);
 }

 const out: Record<string, unknown> = {};
 for (const [key, next] of Object.entries(value)) {
 out[key] = resolveRenderableValue(next, ctx, locals);
 }
 return out;
}

export function extractTriggerName(value: unknown): string | undefined {
 return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function normalizeEventPayload(value: unknown): Record<string, unknown> {
 return isPlainObjectInternal(value)
 ? { ...value }
 : value !== null && value !== undefined
 ? { value }
 : {};
}

export function buildTriggerPayload(
 locals: RenderLocals,
 eventPayload: unknown,
 extra: Record<string, unknown> = {},
): Record<string, unknown> {
 return { ...locals, ...normalizeEventPayload(eventPayload), ...extra };
}

export function invokeTrigger(
 ctx: AtomContext,
 triggerName: string | undefined,
 locals: RenderLocals,
 eventPayload: unknown,
 extra: Record<string, unknown> = {},
): void {
 if (!triggerName) return;
 ctx.fireTrigger(triggerName, buildTriggerPayload(locals, eventPayload, extra));
}

export function normalizeSubmittedValue(
 value: FormDataEntryValue | FormDataEntryValue[],
): unknown {
 if (Array.isArray(value)) {
 return value.map((item) => normalizeSubmittedValue(item));
 }

 return typeof value === "string" ? value : value.name;
}

export function mergeFormValues(
 current: Record<string, unknown>,
 submitted: SubmittedFormValues | undefined,
): Record<string, unknown> {
 const merged: Record<string, unknown> = { ...current };

 for (const [key, raw] of Object.entries(submitted ?? {})) {
 const currentValue = current[key];
 if (typeof currentValue === "boolean") continue;

 merged[key] = normalizeSubmittedValue(raw);
 }

 return merged;
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
 return isPlainObjectInternal(value);
}

export function normalizeBindingPathPublic(input: string): string {
 return normalizeBindingPath(input);
}

export function readPathFromObjectPublic(root: unknown, pathStr: string): unknown {
 return readPathFromObject(root, pathStr);
}

export function shouldUseFallbackPublic(resolved: unknown): boolean {
 return shouldUseFallback(resolved);
}

export function resolveBindingReferencePublic(
 refValue: unknown,
 ctx: AtomContext,
 locals: RenderLocals,
): unknown {
 return resolveBindingReference(refValue, ctx, locals);
}

/**
 * ARIA attributes whose full name is a single lowercase word, so the naive
 * camelCase→kebab hyphenation produces an invalid attribute (e.g.
 * `ariaDescribedBy` → `aria-described-by` instead of `aria-describedby`).
 * React rejects unknown ARIA props, so these must be remapped explicitly.
 */
const ARIA_KEBAB_EXCEPTIONS: Readonly<Record<string, string>> = {
 "aria-described-by": "aria-describedby",
 "aria-labelled-by": "aria-labelledby",
};

/**
 * Normalize React's camel-case ARIA prop names to valid HTML attributes.
 * Explicit kebab-case attributes remain authoritative when both forms exist.
 */
export function normalizeAriaHiddenProp<T extends Record<string, unknown>>(props: T): T {
 const next: Record<string, unknown> = { ...props };
 let changed = false;

 for (const key of Object.keys(props)) {
 if (!/^aria[A-Z]/.test(key)) continue;

 const hyphenated = key
 .replace(/^aria/, "aria-")
 .replace(/[A-Z]/g, (character) => `-${character.toLowerCase()}`)
 .replace(/--/g, "-");
 const kebabKey = ARIA_KEBAB_EXCEPTIONS[hyphenated] ?? hyphenated;

 if (!Object.prototype.hasOwnProperty.call(next, kebabKey)) {
 next[kebabKey] = props[key];
 }
 delete next[key];
 changed = true;
 }

 return changed ? (next as T) : props;
}